/**
 * Supabase Storage helpers for generated reports
 */

import { supabase } from '../../lib/supabase';
import type { ReportFormat } from '../../types/report.types';

const BUCKET = 'generated-reports';

function formatToExtension(format: ReportFormat): string {
  if (format === 'excel') return 'xlsx';
  return format;
}

export function buildStoragePath(userId: string, reportId: string, format: ReportFormat): string {
  return `${userId}/${reportId}.${formatToExtension(format)}`;
}

export async function uploadReportFile(
  userId: string,
  reportId: string,
  format: ReportFormat,
  blob: Blob
): Promise<{ path: string; error: string | null }> {
  const path = buildStoragePath(userId, reportId, format);
  const { error } = await supabase.storage.from(BUCKET).upload(path, blob, {
    upsert: true,
    contentType: blob.type || undefined,
  });
  if (error) {
    return { path, error: error.message };
  }
  return { path, error: null };
}

export async function getSignedDownloadUrl(
  storagePath: string,
  expiresInSeconds = 3600,
  downloadFilename?: string
): Promise<{ url: string | null; error: string | null }> {
  const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(
    storagePath,
    expiresInSeconds,
    downloadFilename ? { download: downloadFilename } : undefined
  );
  if (error) {
    return { url: null, error: error.message };
  }
  return { url: data.signedUrl, error: null };
}

export async function deleteReportFile(storagePath: string): Promise<{ error: string | null }> {
  const { error } = await supabase.storage.from(BUCKET).remove([storagePath]);
  return { error: error?.message ?? null };
}
